import { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const COUNT = 8000;

function keyPoints() {
  const p = new Float32Array(COUNT * 3);
  for (let i = 0; i < COUNT; i++) {
    let x, y;
    const r = Math.random();
    if (r < 0.36) {
      const a = Math.random() * Math.PI * 2;
      const rad = 0.55 + Math.random() * 0.45;
      x = -1.7 + Math.cos(a) * rad;
      y = Math.sin(a) * rad;
    } else if (r < 0.76) {
      x = -0.75 + Math.random() * 3.0;
      y = (Math.random() - 0.5) * 0.34;
    } else if (r < 0.88) {
      x = 1.35 + Math.random() * 0.28;
      y = -0.17 - Math.random() * 0.42;
    } else {
      x = 1.85 + Math.random() * 0.28;
      y = -0.17 - Math.random() * 0.55;
    }
    p[i * 3] = x * 1.05;
    p[i * 3 + 1] = y * 1.05 + 0.2;
    p[i * 3 + 2] = (Math.random() - 0.5) * 0.7;
  }
  return p;
}

function starPoints() {
  const outer = 1.9, inner = outer * 0.42;
  const v = [];
  for (let i = 0; i < 10; i++) {
    const a = Math.PI / 2 + (i * Math.PI) / 5;
    const rad = i % 2 === 0 ? outer : inner;
    v.push([Math.cos(a) * rad, Math.sin(a) * rad]);
  }
  const inside = (x, y) => {
    let c = false;
    for (let i = 0, j = 9; i < 10; j = i++) {
      const [xi, yi] = v[i], [xj, yj] = v[j];
      if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) c = !c;
    }
    return c;
  };
  const p = new Float32Array(COUNT * 3);
  for (let i = 0; i < COUNT; i++) {
    let x, y;
    do {
      x = (Math.random() * 2 - 1) * outer;
      y = (Math.random() * 2 - 1) * outer;
    } while (!inside(x, y));
    p[i * 3] = x;
    p[i * 3 + 1] = y;
    p[i * 3 + 2] = (Math.random() - 0.5) * 0.7;
  }
  return p;
}

const VERT = `
attribute vec3 aKey;
attribute vec3 aBurst;
attribute vec3 aStar;
attribute vec3 aRand;
uniform float uScroll;
uniform float uAssembly;
uniform float uTime;
uniform float uPx;
varying float vBright;
float ez(float t){ return t*t*(3.0-2.0*t); }
void main(){
  vec3 target;
  if (uScroll < 0.35) target = mix(aKey, aBurst, ez(uScroll/0.35));
  else if (uScroll < 0.70) target = mix(aBurst, aStar, ez((uScroll-0.35)/0.35));
  else target = aStar;
  vec3 pos = mix(position, target, uAssembly);
  float sp = 0.6 + aRand.x * 1.6;
  pos += vec3(
    sin(uTime*sp + aRand.y*6.283),
    cos(uTime*sp*0.83 + aRand.z*6.283),
    sin(uTime*0.5 + aRand.x*6.283)
  ) * 0.045;
  vBright = 0.8 + 0.4 * aRand.y;
  vec4 mv = modelViewMatrix * vec4(pos, 1.0);
  gl_PointSize = (1.2 + 1.6 * aRand.z) * uPx * (11.0 / -mv.z);
  gl_Position = projectionMatrix * mv;
}`;

const FRAG = `
uniform vec3 uColor;
varying float vBright;
void main(){
  float d = length(gl_PointCoord - 0.5);
  if (d > 0.5) discard;
  float a = smoothstep(0.5, 0.08, d);
  gl_FragColor = vec4(uColor * vBright, a * 0.85);
}`;

export const ParticleHero = () => {
  const mountRef = useRef(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const mount = mountRef.current;
    const renderer = new THREE.WebGLRenderer({ antialias: false, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(mount.clientWidth, mount.clientHeight);
    mount.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(50, mount.clientWidth / mount.clientHeight, 0.1, 50);
    camera.position.z = 6.5;

    const scatter = new Float32Array(COUNT * 3);
    const burst = new Float32Array(COUNT * 3);
    const rand = new Float32Array(COUNT * 3);
    for (let i = 0; i < COUNT * 3; i++) {
      scatter[i] = (Math.random() - 0.5) * 14;
      burst[i] = (Math.random() - 0.5) * 22;
      rand[i] = Math.random();
    }

    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.BufferAttribute(scatter, 3));
    geo.setAttribute("aKey", new THREE.BufferAttribute(keyPoints(), 3));
    geo.setAttribute("aBurst", new THREE.BufferAttribute(burst, 3));
    geo.setAttribute("aStar", new THREE.BufferAttribute(starPoints(), 3));
    geo.setAttribute("aRand", new THREE.BufferAttribute(rand, 3));

    const uniforms = {
      uScroll: { value: 0 },
      uAssembly: { value: 0 },
      uTime: { value: 0 },
      uPx: { value: Math.min(window.devicePixelRatio, 2) },
      uColor: { value: new THREE.Color("#444CE7") },
    };
    const mat = new THREE.ShaderMaterial({
      vertexShader: VERT,
      fragmentShader: FRAG,
      uniforms,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    scene.add(new THREE.Points(geo, mat));

    gsap.to(uniforms.uAssembly, {
      value: 1, duration: 1.4, ease: "power2.inOut", delay: 0.2,
      onComplete: () => setReady(true),
    });

    const st = ScrollTrigger.create({
      trigger: document.body,
      start: 0,
      end: () => window.innerHeight * 2.2,
      scrub: 0.6,
      onUpdate: (self) => { uniforms.uScroll.value = self.progress; },
    });

    let raf;
    const clock = new THREE.Clock();
    const loop = () => {
      uniforms.uTime.value = clock.getElapsedTime();
      renderer.render(scene, camera);
      raf = requestAnimationFrame(loop);
    };
    loop();

    const onResize = () => {
      camera.aspect = mount.clientWidth / mount.clientHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(mount.clientWidth, mount.clientHeight);
    };
    window.addEventListener("resize", onResize);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
      st.kill();
      geo.dispose();
      mat.dispose();
      renderer.dispose();
      mount.removeChild(renderer.domElement);
    };
  }, []);

  return (
    <div ref={mountRef} className="absolute inset-0" data-testid="particle-canvas">
      {!ready && <div className="shimmer" />}
    </div>
  );
};
